import { Button, ButtonRounded, WContainer, WLink } from '@components/atoms'
import { Close, Search } from '@components/icons'
import { NavCard } from '@components/molecules'
import useTrans from '@hooks/useTrans'
import { isOutsideClick } from '@utils/safe'
import axios from 'axios'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import styles from './SearchModal.module.sass'
import useFindMenu from '@hooks/useFindMenu'
import useTransRoute from '@hooks/useTransRoute'
//import { trackingGAevent, uniformData } from '@utils/gtm'

interface Props {
  className?: string
  searchToggle?: () => void
  closeSearch?: () => void
  open: boolean
}

const SearchModal = (props: Props) => {
  const { className = '', searchToggle, open = false, closeSearch } = props

  const t = useTrans()
  const tRoute = useTransRoute()
  const paginaRicercaUrl = tRoute('/search').href
  const router = useRouter()
  const [searchbarValue, setSearchbarValue] = useState('')
  const [searchEnabled, setSearchEnalbled] = useState(false)
  const [autocompleteValue, setAutocompleteValue] = useState([])
  const [suggestionValue, setSuggestionValue] = useState('')
  const [searchResultValue, setSearchResultValue] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetched, setFetched] = useState(false)
  const searchBarInput = useRef(null)
  const searchModalRef = useRef(null)

  const searchMenu = useFindMenu('ricerca')
  const evidenzaMenu = useFindMenu('evidenza', searchMenu)
  const collezioniMenu = useFindMenu('ricerca-collezioni', searchMenu)

  const handleTrackSearch = (searchTerm: string, label: string, position: 'header' | 'listing') => {
    // trackingGAevent('search_bar', {
    //   searchTerm: uniformData(searchTerm),
    //   position: uniformData(position),
    //   label: uniformData(label),
    //   product: '',
    //   method: '',
    //   type: '',
    // })
  }

  //shortcut to close searchModal by pressing ESC
  useHotkeys('esc', () => closeSearch(), {
    enableOnFormTags: ['input'],
  })

  //function closes menu when a click verifies outside of it
  const handleOutsideClick = (e) => {
    if (
      open &&
      !!searchModalRef.current &&
      !!closeSearch &&
      !e.target.classList.contains('headerSearchTrigger')
    ) {
      if (isOutsideClick(e, searchModalRef)) {
        closeSearch()
        document.body.classList.remove('w-noscroll')
      }
    }
  }

  // Form submit handler
  const handleSearchSubmit = (e) => {
    e.preventDefault()
    searchToggle()
    handleTrackSearch(searchbarValue, '', 'header')
    router.replace(`${paginaRicercaUrl}?q=${searchbarValue}`)
  }

  //funtion to fetch research  results
  const fetchResearchData = async (query: string) => {
    try {
      setLoading(true)
      let params = {
        query,
        suggestions: true,
        lang: router.locale,
      }
      const response = await axios.get(`/api/search`, { params })
      setAutocompleteValue(response.data.autocomplete)
      setSuggestionValue(response.data.suggestion)
      setSearchResultValue(response.data.results)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
      setFetched(true)
    }
  }

  useEffect(() => {
    if (!searchbarValue) {
      setAutocompleteValue([])
      setSuggestionValue('')
      setSearchResultValue([])
      setFetched(false)
    }

    // Se l'input è stringa vuota => ricerca disabilitata
    // All'inizio bisogna inserire almeno 3 caratteri per abilitare la ricerca
    // successivamente anche con meno di 3 caratteri la ricerca è abilitata
    const enabled = !searchbarValue
      ? false
      : !searchEnabled
      ? searchbarValue?.toString()?.trim()?.length >= 3
      : searchEnabled
    setSearchEnalbled(enabled)

    const timeout = setTimeout(() => {
      searchbarValue && enabled && fetchResearchData(searchbarValue)
    }, 500)

    // ! Rimuove il timeout precedente nel caso dovesse essere ritriggerato
    return () => clearTimeout(timeout)
  }, [searchbarValue])

  useEffect(() => {
    if (open) {
      document.body.classList.add('w-noscroll')
      document.addEventListener('mousedown', handleOutsideClick)
      searchBarInput?.current?.focus()
      return () => document?.removeEventListener('mousedown', handleOutsideClick)
    } else {
      document.body.classList.remove('w-noscroll')
    }
  }, [open])

  const goToResultsPage = (e: any) => {
    if (e?.key === 'Enter' && window?.location?.href) {
      handleTrackSearch(searchbarValue, '', 'header')
      window.location.href = `${paginaRicercaUrl}?q=${searchbarValue}`
    }
  }

  const tipologiaMapping = {
    magazine: t('Magazine'),
    colore: t('Colore'),
    prodotto: t('Collezione'),
    pagina: t('Pagina'),
    documento: t('PDF'),
    progetto: t('Progetto'),
    certificazione: t('Certificazione'),
  }
  const getTipologia = (item: string) => {
    return tipologiaMapping[item] || item
  }

  return (
    <div
      className={`${styles.root} ${className} ${open ? styles['root--open'] : ''}`}
      ref={searchModalRef}
    >
      <WContainer variant="xs">
        <div className={styles.layout}>
          <div className={styles.top}>
            <div className={styles.searchbar}>
              <form onSubmit={handleSearchSubmit}>
                <input
                  type="text"
                  autoComplete="off"
                  name="searchBar"
                  id="searchBar"
                  ref={searchBarInput}
                  placeholder={t('Cosa stai cercando?')}
                  value={searchbarValue}
                  onChange={(e) => setSearchbarValue(e.target.value)}
                  className={styles.searchbarInput}
                  onKeyDown={(e) => goToResultsPage(e)}
                ></input>
                <button
                  type="submit"
                  aria-label={t('Conferma')}
                  disabled={!searchEnabled}
                  className={styles.searchSubmit}
                >
                  <Search />
                </button>
              </form>
            </div>
            <ButtonRounded
              icon={<Close />}
              size="xs"
              theme="neutral20"
              onClick={() => searchToggle()}
              className={`${styles.close} ${styles['close--desktop']}`}
            />
          </div>
          {searchEnabled ? (
            <div className={styles.searchResults}>
              {!!autocompleteValue?.length || !!suggestionValue ? (
                <>
                  {autocompleteValue?.length ? (
                    <>
                      <p className={styles.searchResultsTitle}>
                        {t(suggestionValue ? 'Forse stavi cercando' : 'Ricerche suggerite')}
                      </p>
                      <div className={styles.searchResultsList}>
                        {autocompleteValue.map((item, index) => (
                          <WLink
                            key={item.data.id}
                            href={item.data.url}
                            className={styles.searchResultsItem}
                            onClick={() =>
                              handleTrackSearch(searchbarValue, item?.data?.titolo, 'header')
                            }
                          >
                            <span dangerouslySetInnerHTML={{ __html: item.highlight }} />
                            <span>{getTipologia(item.data.tipologia)}</span>
                          </WLink>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <p className={styles.searchResultsTitle}>{t('Forse stavi cercando')}</p>
                      <div className={styles.searchResultsList}>
                        <WLink
                          href={`${paginaRicercaUrl}?q=${suggestionValue}`}
                          className={styles.searchResultsItem}
                          onClick={() => searchToggle()}
                        >
                          {suggestionValue}
                        </WLink>
                      </div>
                    </>
                  )}

                  {searchResultValue.length > 0 && (
                    <Button
                      variant="ghost"
                      label={t('Mostra tutti i risultati')}
                      href={`${paginaRicercaUrl}?q=${searchbarValue}`}
                      className={styles.searchResultsCta}
                      onClick={() => {
                        handleTrackSearch(searchbarValue, t('Mostra tutti i risultati'), 'header')
                        searchToggle()
                      }}
                    />
                  )}
                </>
              ) : !loading && fetched ? (
                <div className={styles.searchResultsEmpty}>
                  <p>{t('Nessun risultato')}</p>
                </div>
              ) : (
                <></>
              )}
            </div>
          ) : (
            <>
              {(!!evidenzaMenu?.children?.length || !!collezioniMenu?.children?.length) && (
                <div className={styles.suggestions}>
                  {!!collezioniMenu?.children?.length && (
                    <div className={`${styles.suggestion} ${styles['suggestion--collezioni']}`}>
                      {!!collezioniMenu.name && (
                        <p className={styles.suggestionTitle}>{collezioniMenu.name}</p>
                      )}
                      <div className={styles.suggestionList}>
                        {collezioniMenu.children.map((item) => (
                          <WLink
                            href={item.url}
                            newPage={item.blank}
                            key={item.pk}
                            className={styles.collezioniItem}
                          >
                            {item.name}
                          </WLink>
                        ))}
                      </div>
                    </div>
                  )}
                  {!!evidenzaMenu?.children?.length && (
                    <div className={`${styles.suggestion} ${styles['suggestion--evidenza']}`}>
                      {!!evidenzaMenu.name && (
                        <p className={styles.suggestionTitle}>{evidenzaMenu.name}</p>
                      )}
                      <div className={styles.suggestionList}>
                        {evidenzaMenu.children.map((item) => (
                          <NavCard item={item} key={item.pk} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </WContainer>
    </div>
  )
}

export default SearchModal
